import { useEffect, useRef, useState } from 'react';

export default function ThreeHurdlesSection() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => {
      if (headingRef.current) {
        observer.unobserve(headingRef.current);
      }
    };
  }, []);

  const hurdles = [
    {
      number: '01',
      title: '発信する「環境」がない',
      subtitle: '防音室と本格機材',
      problems: [
        '家では声を思い切り出せない',
        '機材がなくて、何をそろえればいいかも分からない'
      ],
      solutions: [
        '防音室と本格機材がそろったスタジオで、思い切り声を出せる環境をご用意',
        'マイクやオーディオインターフェースなど、必要な機材はすべて教室側で用意',
        '「まずは手ぶらで来て、実際に触ってみる」ところから始められます'
      ],
      characterImage: 'https://static.readdy.ai/image/f1e8e8819fa7a9a02132dd8ff3c38992/d7828d6a50718d6e11fa0490ef7fc1b8.png'
    },
    {
      number: '02',
      title: '発信する「技術」がない',
      subtitle: '録音・編集技術の習得',
      problems: [
        '録音のやり方が分からない',
        '編集(MIX・ピッチ補正)が難しそうで手をつけられない'
      ],
      solutions: [
        '録音の基本から、ピッチ補正・MIXまで、段階的に丁寧に指導',
        '「ここをクリックして、こう操作する」という具体的な手順を、実際に一緒に進めながら習得',
        '難しい専門用語は使わず、初心者目線で分かりやすく説明します'
      ],
      characterImage: 'https://static.readdy.ai/image/f1e8e8819fa7a9a02132dd8ff3c38992/d0c2564c1ff0dc8f75c989f00ca384ce.png'
    },
    {
      number: '03',
      title: '発信する「自信」がない',
      subtitle: '自分の声・歌への不安',
      problems: [
        '自分の歌声に自信がない',
        '「下手だ」と言われるのが怖い',
        '他の人と比べて落ち込んでしまう'
      ],
      solutions: [
        '「上手さ」だけでなく、あなたの声の良さ・個性を言語化してお伝え',
        '「ここが良い」「ここを伸ばせる」と具体的にフィードバック',
        'アンチコメントへの向き合い方も、講師が実体験をもとにサポート'
      ],
      characterImage: 'https://static.readdy.ai/image/f1e8e8819fa7a9a02132dd8ff3c38992/19f07afbe555541b8ceb5c5792766892.png'
    }
  ];

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* セクション見出し */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            ref={headingRef}
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            歌ってみたが投稿できない<br className="md:hidden" />3つのハードル
          </h2>
        </div>

        <div className="space-y-10 sm:space-y-12 md:space-y-16">
          {hurdles.map((hurdle, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-orange-50 to-white p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-orange-100 relative overflow-hidden"
            >
              {/* キャラクター画像 - 右側全体に配置 */}
              <div className="absolute top-0 -right-8 sm:right-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 opacity-20 sm:opacity-40 lg:opacity-100 lg:block pointer-events-none">
                <img
                  src={hurdle.characterImage}
                  alt="キャラクター"
                  className="w-full h-full object-contain"
                />
              </div>

              {/* テキストエリア */}
              <div className="space-y-4 sm:space-y-6 relative z-10">
                {/* ナンバーとタイトル */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="inline-block bg-gradient-to-br from-orange-500 to-red-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-xl sm:rounded-2xl shadow-lg">
                    <span className="text-2xl sm:text-3xl md:text-4xl font-black">{hurdle.number}</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-gray-800 leading-tight">
                    {hurdle.title}
                  </h3>
                  <div className="inline-block bg-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full shadow-md border-2 border-orange-200">
                    <p className="text-base sm:text-lg font-bold text-orange-600">{hurdle.subtitle}</p>
                  </div>
                </div>

                {/* よくあるお悩み */}
                <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-md border-2 border-gray-100">
                  <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    よくあるお悩み
                  </h4>
                  <ul className="space-y-2">
                    {hurdle.problems.map((problem, idx) => (
                      <li key={idx} className="flex items-start gap-2 sm:gap-3 text-gray-700">
                        <span className="text-orange-500 font-bold flex-shrink-0">•</span>
                        <span className="text-sm sm:text-base">{problem}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Size Music Schoolでの解決策 */}
                <div className="bg-white border-2 border-orange-500 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-500 rounded-full">
                      <i className="ri-lightbulb-flash-line text-lg sm:text-xl text-white"></i>
                    </div>
                    <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
                      Size Music Schoolでの解決策
                    </h4>
                  </div>
                  <ul className="space-y-3 sm:space-y-4">
                    {hurdle.solutions.map((solution, idx) => (
                      <li key={idx} className="flex items-start gap-3 sm:gap-4 bg-orange-50 p-3 sm:p-4 rounded-lg">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mt-0.5">
                          <i className="ri-check-line text-sm sm:text-base text-white"></i>
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{solution}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
